import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Layout from '@/components/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { FileText, Upload, Download, Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Documents() {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [documentCategory, setDocumentCategory] = useState('personal');

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/documents/my`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDocuments(response.data);
    } catch (error) {
      toast.error('Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('document_category', documentCategory);

      await axios.post(`${API}/documents/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        params: { document_category: documentCategory }
      });

      toast.success('Document uploaded successfully');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (docId, fileName) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/documents/${docId}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const byteCharacters = atob(response.data.file_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: response.data.content_type });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('Document downloaded');
    } catch (error) {
      toast.error('Failed to download document');
    }
  };

  return (
    <Layout>
      <div className="space-y-8" data-testid="documents-page">
        <div>
          <h1 className="text-4xl font-bold tracking-tight">My Documents</h1>
          <p className="text-muted-foreground mt-2">Upload and manage your documents</p>
        </div>

        <Card data-testid="upload-card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Upload className="mr-2 h-5 w-5" />
              Upload Document
            </CardTitle>
            <CardDescription>Upload your personal documents or ID proofs</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Document Category</label>
                <select
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                  value={documentCategory}
                  onChange={(e) => setDocumentCategory(e.target.value)}
                  data-testid="select-category"
                >
                  <option value="personal">Personal Document</option>
                  <option value="id_proof">ID Proof (Aadhar, PAN, etc.)</option>
                  <option value="address_proof">Address Proof</option>
                  <option value="education">Educational Certificate</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="flex items-center gap-4">
                <Input
                  type="file"
                  onChange={handleUpload}
                  disabled={uploading}
                  data-testid="file-input"
                />
                {uploading && <span className="text-sm text-muted-foreground">Uploading...</span>}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-testid="documents-list-card">
          <CardHeader>
            <CardTitle className="flex items-center">
              <FileText className="mr-2 h-5 w-5" />
              My Documents
            </CardTitle>
            <CardDescription>View and download your uploaded documents</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">Loading...</div>
            ) : documents.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                No documents uploaded yet
              </div>
            ) : (
              <div className="space-y-4">
                {documents.map((doc) => (
                  <div
                    key={doc.id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/20 transition-colors"
                    data-testid="document-item"
                  >
                    <div className="flex items-center space-x-4">
                      <FileText className="h-8 w-8 text-accent" />
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{doc.document_name}</p>
                          <span className="text-xs px-2 py-1 bg-secondary rounded-full">
                            {doc.document_category || 'personal'}
                          </span>
                          {doc.uploaded_by_role === 'admin' && (
                            <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full">
                              From Admin
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-muted-foreground">
                          Uploaded on {new Date(doc.uploaded_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDownload(doc.id, doc.document_name)}
                      data-testid="download-button"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
